/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * UPLOAD BATCH (owner decisions 1 to 3, 02/11/2026). Edgewater: one file of nine
 * failed with "Signed upload failed: Failed to fetch", and the loop stopped, so
 * the eight after it were never tried, and the client was shown that technical
 * text. Now:
 *  1. every file is uploaded and reported on its own; one failure never stops
 *     the rest (documents first in src/App.jsx, Migrate Client in
 *     src/MLRODashboard.jsx);
 *  2. documents first does not start reading while a failed file is still in
 *     the list (readingMayStart): the client retries it or removes it;
 *  3. the whole file is read once in the browser before it is sent, and the
 *     bytes read are what is uploaded (readWhole, storeFile), so Sentry records
 *     whether the file could not be opened from the computer or the network
 *     failed, with its size; the client's message follows from that, in plain
 *     words. The technical detail goes to Sentry only. Sentry events carry the
 *     file's extension and sizes, never its name (src/sentry.js: no personal
 *     data; a client's file name can carry a person's or company's name).
 *
 * Review fixes (02/11/2026): a retry after a failed record step resends only
 * the record step with the stored path (one object, one row); a new copy
 * replaces the failed file even when the browser reads its size as 0; reading
 * continues by itself once no failed file remains (DF_CONTINUE_LINE); a session
 * or security code failure has its own message; Try again only where it helps.
 *
 * Pure apart from reading the file; no React, no network.
 */

export const UPLOAD_FAILURE = Object.freeze({
  UNREADABLE: 'file_unreadable',   // the browser could not read the file from disk
  NETWORK: 'network',              // the request produced no readable answer ("Failed to fetch")
  STORAGE: 'storage_refused',      // storage answered with an error
  SIGN: 'sign_failed',             // the upload could not be prepared on the server
  RECORD: 'record_failed',         // stored, but the record step failed (an orphaned upload)
  REFUSED: 'refused',              // the server refused the file from its content
});

// An error that carries its failure kind and the technical detail for Sentry.
export function uploadFailure(kind, detail, extra = {}) {
  const e = new Error(String(detail || kind));
  e.uploadFailure = kind;
  Object.assign(e, extra);
  return e;
}

export const failureKindOf = (e) => (e && e.uploadFailure) || UPLOAD_FAILURE.NETWORK;

// Owner decision 3: the whole file is read before it is sent (files are capped
// at 30 MB). A file the browser can no longer read (an online only copy in a
// synced folder, a locked file, one moved after it was chosen, one that fails
// part way through) fails here, not inside the upload. null when it cannot.
export async function readWhole(file) {
  try {
    if (!file || typeof file.arrayBuffer !== 'function') return null;
    return await file.arrayBuffer();
  } catch (_) { return null; }
}
// A small check, used again after a failed upload to tell the network from a
// file that stopped being readable.
export async function fileReadable(file) {
  try {
    if (!file || typeof file.slice !== 'function') return false;
    await file.slice(0, 1).arrayBuffer();
    return true;
  } catch (_) { return false; }
}

export const fileSizeKb = (file) => Math.round(((file && file.size) || 0) / 1024);
// The extension only, for Sentry (never the name).
export const fileExt = (file) => { const m = /\.([A-Za-z0-9]{1,10})$/.exec(String((file && file.name) || '')); return m ? m[1].toLowerCase() : ''; };

// Owner decision 1: each file on its own. uploadOne(file) resolves to the
// file's entry or throws; the next file is tried either way.
export async function uploadEach(files, uploadOne, { onProgress } = {}) {
  const list = Array.from(files || []);
  const results = [];
  for (let i = 0; i < list.length; i++) {
    const file = list[i];
    if (onProgress) { try { onProgress(i, list.length, file); } catch (_) {} }
    try {
      const entry = await uploadOne(file);
      results.push({ file, ok: true, entry });
    } catch (e) {
      // path: set when storage worked and only the record step failed, so a retry
      // records that stored file instead of storing the file a second time.
      results.push({ file, ok: false, kind: failureKindOf(e), refusal: e && e.refusal ? String(e.message || '') : null, path: (e && e.path) || null, detail: String((e && e.message) || e || 'upload failed') });
    }
  }
  return results;
}

// Owner decision 2: reading starts only when no file in the list is still
// failed or not yet sent (stateOf(file).state is 'uploaded' for every one).
// An empty list holds nothing back (Invite with documents may go on with the
// firm's documents alone; the caller decides whether there is anything to read).
export function readingMayStart(files, stateOf) {
  return !Array.from(files || []).some(f => { const s = stateOf(f); return !s || s.state !== 'uploaded'; });
}

// storage-js: a StorageApiError carries the HTTP status storage answered with;
// a StorageUnknownError means fetch itself rejected (no readable answer).
export function storageErrorKind(err) {
  const name = String((err && err.name) || '');
  const status = err && (err.status || err.statusCode);
  return (status && !/UnknownError/.test(name)) ? UPLOAD_FAILURE.STORAGE : UPLOAD_FAILURE.NETWORK;
}

// The size each file had when it was chosen, for Sentry (a file that becomes
// unreadable can then read as 0 bytes).
const picked = new WeakMap();
export function notePicked(files) { for (const f of Array.from(files || [])) { try { if (f && typeof f === 'object' && !picked.has(f)) picked.set(f, f.size || 0); } catch (_) {} } }
export const pickedSizeKb = (file) => (file && typeof file === 'object' && picked.has(file)) ? Math.round(picked.get(file) / 1024) : null;

// The bytes read, as a file with the same name and type, for the upload.
export function inMemoryCopy(file, bytes) {
  const type = (file && file.type) || '';
  if (typeof File === 'function') return new File([bytes], String((file && file.name) || 'document'), { type });
  return new Blob([bytes], { type });
}

// One file to storage through a signed upload. sign(file) resolves to
// { path, token } or { error }; put(path, token, body) to { error }, where body
// is the in memory copy of the file read before. Throws an uploadFailure with
// its kind; report(error, kind, extra) is told first.
export async function storeFile(file, { sign, put, report }) {
  let kind = UPLOAD_FAILURE.NETWORK;
  const sizeKbAtStart = fileSizeKb(file);
  try {
    const bytes = await readWhole(file);
    if (!bytes) { kind = UPLOAD_FAILURE.UNREADABLE; throw new Error('The file could not be read in the browser'); }
    let s;
    try { s = await sign(file); } catch (e) { kind = UPLOAD_FAILURE.NETWORK; throw new Error('Could not reach the upload service: ' + ((e && e.message) || e)); }
    if (!s || !s.path || !s.token) { kind = UPLOAD_FAILURE.SIGN; throw new Error('Could not mint a signed upload URL: ' + ((s && s.error) || 'no answer')); }
    const out = await put(s.path, s.token, inMemoryCopy(file, bytes));
    const upErr = out && out.error;
    if (upErr) {
      kind = storageErrorKind(upErr);
      // The network failed, or the file stopped being readable while it was sent.
      if (kind === UPLOAD_FAILURE.NETWORK && !(await fileReadable(file))) kind = UPLOAD_FAILURE.UNREADABLE;
      throw new Error('Signed upload failed: ' + (upErr.message || upErr));
    }
    return s.path;
  } catch (e) {
    const detail = String((e && e.message) || e);
    if (report) { try { report(new Error(detail), kind, { fileSizeKb: fileSizeKb(file), fileSizeKbAtStart: sizeKbAtStart, fileSizeKbAtPick: pickedSizeKb(file), ext: fileExt(file) }); } catch (_) {} }
    throw uploadFailure(kind, detail);
  }
}

// The record step's answer for a stored file. null when it was recorded; else
// the failure: a refusal from the file's content (said in the shared wording;
// the server removed the stored file), or record_failed (an orphaned upload,
// never passed over in silence) carrying the stored path for the retry. An
// orphan refused for its path carries no path: that path can never record.
export function recordFailure(name, res, path = null) {
  const body = (res && res.body) || {};
  if (res && res.ok && !body.orphan) return null;
  if (body.refused && body.error) return uploadFailure(UPLOAD_FAILURE.REFUSED, body.error, { refusal: true });
  return uploadFailure(UPLOAD_FAILURE.RECORD, 'Could not record ' + name + ': ' + ((res && res.error) || body.error || ('status ' + (res && res.status))), { path: body.orphan ? null : path });
}

// Try again helps only where the same file can go through: not for a file the
// server refused from its content, nor one the browser cannot open (the
// client chooses a new copy instead, which takes its place).
export const retryHelps = (kind) => kind !== UPLOAD_FAILURE.REFUSED && kind !== UPLOAD_FAILURE.UNREADABLE;

// Review fix S2: the failed files a newly chosen set replaces. A new copy is
// matched on its name and the size the failed file had when it was picked (the
// browser can read an unreadable file's size as 0), or on its name alone when
// exactly one failed file has that name (a copy made again can differ in size).
export function failedReplacedBy(prevFiles, incoming, stateOf) {
  const failed = Array.from(prevFiles || []).filter(f => (stateOf(f) || {}).state === 'failed');
  const out = new Set();
  for (const n of Array.from(incoming || [])) {
    const same = failed.filter(f => f.name === n.name && !out.has(f));
    if (!same.length) continue;
    const bySize = same.find(f => pickedSizeKb(f) === fileSizeKb(n) || (f.size && f.size === n.size));
    if (bySize) out.add(bySize);
    else if (same.length === 1 && failed.filter(f => f.name === n.name).length === 1) out.add(same[0]);
  }
  return [...out];
}

// Review fix S3 (return visit): the uploads the case already recorded for this
// intake, from the server (save-document intake-uploads), as list entries shown
// "Uploaded" and never sent again. rows: [{ name, size, path, mime }].
export function recordedEntries(rows) {
  return (Array.isArray(rows) ? rows : []).filter(r => r && r.name && r.path).map(r => {
    const placeholder = { name: String(r.name), size: Number(r.size) || 0, type: r.mime || '', recorded: true };
    return { file: placeholder, state: { state: 'uploaded', entry: { name: String(r.name), path: String(r.path), mime: r.mime || null, size: Number(r.size) || 0 }, recorded: true } };
  });
}

const nameList = (names) => names.length <= 1 ? (names[0] || '')
  : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];

// The client's message for the files that did not upload. failed: [{ name,
// kind, refusal }]; uploadedCount: how many other files in the list uploaded.
// Plain words only: no status, no error text. One file that did not upload is
// the owner's message word for word. A file the browser could not open keeps
// that wording and says it could not be opened (Try again cannot help it). A
// session or security code failure says to reload the page (a new copy cannot
// help it). A refusal is the shared refusal line, said as it is.
export function uploadFailureMessage(failed, uploadedCount = 0) {
  const all = Array.isArray(failed) ? failed.filter(Boolean) : [];
  const refusals = all.filter(f => f.kind === UPLOAD_FAILURE.REFUSED && f.refusal).map(f => f.refusal);
  const unreadable = all.filter(f => f.kind === UPLOAD_FAILURE.UNREADABLE);
  const session = all.filter(f => f.kind === UPLOAD_FAILURE.SIGN);
  const rest = all.filter(f => !refusals.includes(f.refusal) && f.kind !== UPLOAD_FAILURE.UNREADABLE && f.kind !== UPLOAD_FAILURE.SIGN && !(f.kind === UPLOAD_FAILURE.REFUSED && f.refusal));
  const groups = [];
  const names = (g) => nameList(g.map(f => f.name));
  if (rest.length === 1) groups.push([`We could not upload ${rest[0].name}.`, ' Please try this one again. If it still does not upload, save a new copy of the file on your computer (for example on your desktop) and choose that copy instead.']);
  else if (rest.length > 1) groups.push([`We could not upload ${names(rest)}.`, ' Please try these again. If they still do not upload, save a new copy of each file on your computer (for example on your desktop) and choose those copies instead.']);
  if (unreadable.length === 1) groups.push([`We could not upload ${unreadable[0].name}. We could not open it from this computer.`, ' Please save a new copy of the file on your computer (for example on your desktop) and choose that copy instead.']);
  else if (unreadable.length > 1) groups.push([`We could not upload ${names(unreadable)}. We could not open them from this computer.`, ' Please save a new copy of each file on your computer (for example on your desktop) and choose those copies instead.']);
  if (session.length) groups.push([`We could not upload ${names(session)} just now.`, ` Please reload this page (enter your security code again if you are asked for one), then try ${session.length === 1 ? 'it' : 'them'} again.`]);
  const parts = groups.map(([lead, advice], i) => lead + (i === 0 && uploadedCount > 0 ? ' Your other documents were uploaded.' : '') + advice);
  for (const r of refusals) parts.push(r);
  return parts.join(' ');
}

// The short state shown beside a file in the list.
export function failedFileLabel(kind) {
  if (kind === UPLOAD_FAILURE.UNREADABLE) return 'Could not be opened from this computer';
  if (kind === UPLOAD_FAILURE.REFUSED) return 'Cannot be accepted';
  return 'Did not upload';
}

// Shown while reading continues by itself after the last failed file was
// uploaded or removed (owner decision 2: no second Submit).
export const DF_CONTINUE_LINE = 'Every document has uploaded. We are starting to read them now.';

// Owner, 03/11/2026: shown on a return visit when the case already holds uploads
// from an intake that was never submitted (the last upload did not finish).
export const DF_RETURN_LINE = 'Your last upload did not finish. Please check that every document you meant to send is listed.';
// Shown under the list while a failed file remains (rule 4: the hold is said).
export const READING_HELD_LINE = 'We start reading your documents once every document in the list has uploaded. Try again, or remove a document to go on without it.';
// Staging test 03/11/2026: a file that could not be opened (or was refused) has
// no Try again, so the held line must not offer one. Choose a new copy instead.
export const READING_HELD_LINE_NO_RETRY = 'We start reading your documents once every document in the list has uploaded. Choose a new copy of the document, or remove it to go on without it.';
/** The held line for this list: Try again only when a failed file offers it. */
export function readingHeldLine(files, stateOf) {
  const failed = (files || []).map((f) => stateOf(f) || {}).filter((st) => st.state === 'failed');
  return failed.some((st) => retryHelps(st.kind)) ? READING_HELD_LINE : READING_HELD_LINE_NO_RETRY;
}

// Migrate Client (officer): the files that did not upload, named. Plain words;
// the detail is in Sentry.
export function migrateFailedLine(names) {
  const n = Array.isArray(names) ? names.filter(Boolean) : [];
  if (!n.length) return '';
  return `${n.length} document${n.length === 1 ? '' : 's'} did not upload: ${nameList(n)}.`;
}
// Migrate Client: files that cannot be opened are found before the case is
// created, so no case (and no charge) is made for a set that cannot upload.
export function migrateUnreadableLine(names) {
  const n = Array.isArray(names) ? names.filter(Boolean) : [];
  if (!n.length) return '';
  return `${n.length === 1 ? 'This document' : 'These documents'} could not be opened from this computer: ${nameList(n)}. Save a new copy of ${n.length === 1 ? 'it' : 'each'} on your computer and choose ${n.length === 1 ? 'that copy' : 'those copies'}. No case was created.`;
}
// Migrate Client, some files did not upload: the officer adds them and reads
// them into the migrated file (Reprocess), or the file is assembled without them.
export const MIGRATE_ADD_THEN_REPROCESS = ' Open the case, add them with Manual Upload, then press Reprocess so the file is assembled with them.';
// Migrate Client, none uploaded: the empty case is kept for another try.
export const MIGRATE_NONE_UPLOADED = ' The case was created and is kept with no documents. Press Try the upload again to upload into the same case (no new case and no new charge), or Discard the empty case.';
